"use client";

import * as React from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@modernatc/ui/cn";

export interface UserMenuLink {
  href: string;
  label: string;
  hint?: string;
  external?: boolean;
}

interface UserMenuProps {
  email: string;
  tierLabel: string;
  links: ReadonlyArray<UserMenuLink>;
  /**
   * The Sign-out button is rendered inside its own form (server action)
   * by the parent so this component stays presentation-only.
   */
  signOutSlot: React.ReactNode;
}

/**
 * Compact "you're signed in" dropdown for the marketing-site nav.
 * Closes on outside click, Escape, or after any link is clicked.
 * Server Component renders the trigger label and link list; this
 * client component just owns the open/close state.
 */
export function UserMenu({ email, tierLabel, links, signOutSlot }: UserMenuProps) {
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    function onPointer(e: MouseEvent | TouchEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("touchstart", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("touchstart", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // The dropdown shows just the local part of the email so the trigger
  // stays compact at narrow widths. Hover/focus shows the full address
  // via the title attribute.
  const localPart = email.split("@")[0] ?? email;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={email}
        className={cn(
          "inline-flex items-center gap-2 rounded-[var(--radius)]",
          "border border-[var(--color-panel-edge)] bg-[var(--color-panel)]",
          "px-3 py-1.5 text-sm text-[var(--color-ink)]",
          "hover:border-[var(--color-amber)]/60 hover:bg-[var(--color-panel-raised)]",
          "transition-colors",
        )}
      >
        <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-[var(--color-amber)]/15 text-callsign text-[10px] text-[var(--color-amber-bright)]">
          {(localPart[0] ?? "?").toUpperCase()}
        </span>
        <span className="hidden max-w-[140px] truncate sm:inline">{localPart}</span>
        <ChevronDown
          aria-hidden
          className={cn(
            "size-3.5 text-[var(--color-ink-dim)] transition-transform",
            open && "rotate-180",
          )}
        />
      </button>

      {open ? (
        <div
          role="menu"
          className={cn(
            "absolute right-0 top-full z-50 mt-2 w-64 origin-top-right",
            "rounded-[var(--radius-md)] border border-[var(--color-panel-edge)]",
            "bg-[var(--color-panel-deep)]/95 p-1.5 shadow-[var(--shadow-panel)]",
            "backdrop-blur-md",
          )}
        >
          <div className="px-3 py-2.5">
            <p className="text-callsign text-[10px] text-[var(--color-ink-muted)]">
              Signed in
            </p>
            <p className="text-mono mt-0.5 truncate text-sm text-[var(--color-ink)]">
              {email}
            </p>
            <p className="mt-1 text-callsign text-[10px] text-[var(--color-amber)]">
              ▸ {tierLabel}
            </p>
          </div>

          <div className="my-1 h-px bg-[var(--color-panel-edge)]" />

          {links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={() => setOpen(false)}
              {...(link.external ? { target: "_self", rel: "noreferrer" } : {})}
              className={cn(
                "flex flex-col gap-0.5 rounded-[var(--radius-sm)] px-3 py-2",
                "text-sm text-[var(--color-ink)]",
                "hover:bg-[var(--color-panel-raised)]",
              )}
              role="menuitem"
            >
              <span className="font-medium">{link.label}</span>
              {link.hint ? (
                <span className="text-xs text-[var(--color-ink-muted)]">{link.hint}</span>
              ) : null}
            </a>
          ))}

          <div className="my-1 h-px bg-[var(--color-panel-edge)]" />

          <div className="px-1 pb-1 pt-0.5" role="none">
            {signOutSlot}
          </div>
        </div>
      ) : null}
    </div>
  );
}
